import {useUtilityData} from "../../utilities/contexts";
import withUtilities from "../../hooks/withUtilities";
import {useDispatch, useSelector} from "react-redux";
import {Button, Input} from "@headlessui/react";
import React, {useMemo, useState} from "react";
import {PmsItemTitle} from "./ItemTitle";
import {updatePmsSearchDetails} from "../../redux/actions/pmsSearch";

const YourDetailsForm = ({onNext}) => {
  const pmsSearch = useSelector((state) => state.pmsSearch);
  const utilities = useUtilityData((utilityData) => utilityData);
  const user = useSelector((state) => state.user.userDetails);
  const dispatch = useDispatch();

  const [errors, setErrors] = useState({});
  const [sectionValues, setSectionValues] = useState({
    age: pmsSearch.age ?? '',
    gender: pmsSearch.gender?.length > 0 ? utilities.genders.map(gender =>
      gender.value === pmsSearch.gender[0].value ?
        pmsSearch.gender[0]
        : gender) : utilities.genders,
    contactNumber: pmsSearch.contactNumber ?? '',
    sexuality: pmsSearch.sexuality ?? '',
    university: pmsSearch.university ?? '',
  });

  const tryNext = () => {
    if(validate()) {
      if (onNext) {
        onNext();
      }
    }
  }

  const validate = () => {
    const newErrors = {};

    if(!sectionValues.age || sectionValues.age.trim() === '' || isNaN(parseInt(sectionValues.age, 10))) {
      newErrors.age = true;
    }

    if(!sectionValues.contactNumber || sectionValues.contactNumber.trim() === '') {
      newErrors.contact = true;
    }

    if(!sectionValues.university || sectionValues.university.trim() === '') {
      newErrors.university = true;
    }

    if(Object.keys(newErrors).length === 0) {
      completeStep();
      return true;
    }

    setErrors(newErrors);
    return false;
  }

  const completeStep = () => {
    dispatch(updatePmsSearchDetails({
      ...sectionValues,
      gender: sectionValues.gender.filter(gender => gender.selected),
    }));
  }

  const updateItem = (key, value, selected) => {
    setErrors({});
    const tmpState = {...sectionValues};

    switch(key) {
      case 'gender':
        tmpState.gender = utilities.genders;
        if(value !== null && selected) {
          tmpState.gender = tmpState.gender.map(gender => gender.value === value ? {
            ...gender,
            selected: true,
          } : gender);
        }
        break;
      default:
        tmpState[key] = value;
        break;
    }

    setSectionValues(tmpState);
  }

  const genderSelected = useMemo(() => {
    return sectionValues.gender.filter(gender => gender.selected).length > 0;
  }, [sectionValues.gender])

  return (
    <div className='w-full flex flex-col px-2 mt-6'>
      <div className='flex items-center w-full gap-6 mt-2 py-6 mb-6 border-b'>
        <div className='flex flex-col'>
          <span className='text-brand-text text-sm'>Client name</span>
          <span className='text-brand-alt font-light'>{user.fullname}</span>
        </div>
        <div className='flex flex-col'>
          <span className='text-brand-text text-sm'>Contact email</span>
          <span className='text-brand-alt font-light'>{user.email}</span>
        </div>
      </div>
      <div className='w-full flex flex-col gap-4'>
        <div className='flex flex-col w-full gap-1'>
          <PmsItemTitle required>
            Contact number
          </PmsItemTitle>
          <Input
            onChange={e => updateItem('contactNumber', e.target.value)}
            name='contact'
            type='text'
            className={`w-full bg-white appearance-none border border-gray-300 placeholder:text-gray-300 p-2 rounded-lg ${errors.contact && 'border-red-500 bg-red-50 placeholder:text-grey-500'} transition`}
            placeholder='Enter your contact number'
            value={sectionValues.contactNumber}
          />
          {errors.contact && (
            <span className='text-red-500 text-xs'>Please provide a contact number</span>
          )}
        </div>
        <div className='flex flex-col w-full gap-1'>
          <PmsItemTitle required>
            Your age
          </PmsItemTitle>
          <Input
            onChange={e => updateItem('age', e.target.value)}
            name='age'
            type='number'
            className={`w-full bg-white appearance-none border border-gray-300 placeholder:text-gray-300 p-2 rounded-lg ${errors.age && 'border-red-500 bg-red-50 placeholder:text-grey-500'} transition`}
            placeholder='Enter your age'
            value={sectionValues.age}
          />
          {errors.age && (
            <span className='text-red-500 text-xs'>Please enter your age</span>
          )}
        </div>
        <div className='flex flex-col w-full gap-1'>
          <PmsItemTitle required>
            Your gender
          </PmsItemTitle>
          <div className='flex flex-wrap gap-1.5'>
            <Button
              onClick={() => updateItem('gender', null, null)}
              className={`px-4 py-2 ${!genderSelected ? 'bg-brand-alt' : 'bg-brand-off'} flex flex-1 justify-center gap-2 items-center rounded-full opacity-1 cursor-pointer hover:opacity-[0.7] transition`}>
              <p
                className={`${!genderSelected ? 'text-white' : 'text-brand-text'} text-sm font-bold transition w-max flex-nowrap text-nowrap`}>Other/Prefer
                not to say</p>
            </Button>
            {sectionValues.gender.map(gender => (
              <Button
                onClick={() => updateItem('gender', gender.value, !gender.selected)}
                className={`px-4 py-2 ${gender.selected ? 'bg-brand-alt' : 'bg-brand-off'} flex flex-1 justify-center gap-2 items-center rounded-full opacity-1 cursor-pointer hover:opacity-[0.7] transition`}
                key={`gender_${gender.value}`}>
                <p
                  className={`${gender.selected ? 'text-white' : 'text-brand-text'} text-sm font-bold transition`}>{gender.label}</p>
              </Button>
            ))}
          </div>
        </div>
        <div className='flex flex-col w-full gap-1'>
          <PmsItemTitle>
            Your sexual orientation
          </PmsItemTitle>
          <Input
            onChange={e => updateItem('sexuality', e.target.value)}
            name='sexuality'
            type='text'
            className='w-full appearance-none border border-gray-300 placeholder:text-gray-300 p-2 rounded-lg'
            placeholder='Your sexuality'
            value={sectionValues.sexuality}
          />
          <span className='text-brand-alt text-sm'>
            You can share this with us if you feel it is important to your search, otherwise you are welcome to leave this blank
          </span>
        </div>
        <div className='flex flex-col w-full gap-1'>
          <PmsItemTitle required>
            Your university
          </PmsItemTitle>
          <Input
            onChange={e => updateItem('university', e.target.value)}
            name='university'
            type='text'
            className={`w-full bg-white appearance-none border border-gray-300 placeholder:text-gray-300 p-2 rounded-lg ${errors.university && 'border-red-500 bg-red-50 placeholder:text-grey-500'} transition`}
            placeholder='University at which you are enrolled'
            value={sectionValues.university}
          />
          {errors.university && (
            <span className='text-red-500 text-xs'>Please provide your university you are enrolled at</span>
          )}
        </div>
      </div>
      <div className='py-4 w-full flex justify-end'>
        <Button onClick={tryNext} className='bg-brand text-white font-bold px-4 py-2 rounded-full hover:bg-white hover:text-brand transition'>
          Next step
        </Button>
      </div>
    </div>
  )
}

export default withUtilities(YourDetailsForm);
